import { exactSessionExpPoint } from '@/core/session-exp-counter'
import type { GameStateSnapshot, ValidatedObservation } from '@/domain/types'
import type { GamePreset } from '@/presets/types'

export type SessionStartBaselineStatus =
  | 'ready'
  | 'stale'
  | 'exp-confirming'
  | 'exp-incomplete'
  | 'level-unconfirmed'
  | 'table-conflict'
  | 'capture-unavailable'

export type SessionStartBaselineEvaluation =
  | {
    readonly status: 'ready'
    readonly baseline: GameStateSnapshot
    readonly sourceAt: number
  }
  | {
    readonly status: Exclude<SessionStartBaselineStatus, 'ready'>
    readonly baseline: null
    readonly sourceAt: number | null
  }

export interface SessionStartBaselineOptions {
  readonly now: number
  readonly captureGeneration: number
  readonly observationGeneration: number
  readonly maximumAgeMs: number | null
  readonly minimumCapturedAt?: number
  readonly mesosMaximumAgeMs: number
}

export function evaluateSessionStartBaseline(
  preset: GamePreset,
  state: GameStateSnapshot | null,
  observation: ValidatedObservation | null,
  options: SessionStartBaselineOptions,
): SessionStartBaselineEvaluation {
  if (options.observationGeneration !== options.captureGeneration) {
    return unavailable('capture-unavailable')
  }
  if (!state || !observation) return unavailable('stale')

  const levelSourceAt = observation.level.capturedAt
  if (observation.level.availability === 'unavailable'
    || observation.level.value === null
    || !observation.accepted.level
    || state.level === null
    || state.level !== observation.level.value
    || state.observedAt?.level !== levelSourceAt) {
    return unavailable('level-unconfirmed', levelSourceAt)
  }
  if (!isFreshSource(levelSourceAt, options)) return unavailable('stale', levelSourceAt)

  const sourceAt = observation.exp.capturedAt
  const exp = observation.exp.value
  const expDiagnostic = observation.diagnostics.metrics?.exp?.status
  if (observation.exp.availability === 'unavailable' || !exp) {
    return unavailable(expDiagnostic === 'confirming' ? 'exp-confirming' : 'exp-incomplete', sourceAt)
  }
  if (exp.absolute === null || exp.ratio === null) {
    return unavailable(expDiagnostic === 'confirming' ? 'exp-confirming' : 'exp-incomplete', sourceAt)
  }
  if (!isFreshSource(sourceAt, options)) {
    return unavailable('stale', sourceAt)
  }
  if (exp.estimated) return unavailable('exp-incomplete', sourceAt)
  const candidateState = { ...state, level: observation.level.value, exp }
  if (!exactSessionExpPoint(preset, candidateState)) return unavailable('table-conflict', sourceAt)
  if (!observation.accepted.exp) {
    return unavailable('exp-confirming', sourceAt)
  }
  if (state.observedAt?.exp !== sourceAt
    || state.exp.absolute !== exp.absolute
    || state.exp.ratio !== exp.ratio) {
    return unavailable('exp-confirming', sourceAt)
  }
  if (!exactSessionExpPoint(preset, state)) {
    return unavailable(state.exp.estimated ? 'exp-incomplete' : 'table-conflict', sourceAt)
  }

  const mesosFresh = observation.accepted.mesos
    && observation.mesos.confirmation === 'consensus'
    && observation.mesos.value !== null
    && state.mesos === observation.mesos.value
    && state.observedAt?.mesos === observation.mesos.capturedAt
    && options.now >= observation.mesos.capturedAt
    && options.now - observation.mesos.capturedAt <= options.mesosMaximumAgeMs

  return {
    status: 'ready',
    sourceAt,
    baseline: {
      ...state,
      mesos: mesosFresh ? state.mesos : null,
      observedAt: {
        ...state.observedAt,
        mesos: mesosFresh ? observation.mesos.capturedAt : null,
      },
    },
  }
}

function isFreshSource(sourceAt: number, options: SessionStartBaselineOptions): boolean {
  return Number.isFinite(sourceAt)
    && options.now >= sourceAt
    && (options.minimumCapturedAt === undefined || sourceAt >= options.minimumCapturedAt)
    && (options.maximumAgeMs === null || options.now - sourceAt <= options.maximumAgeMs)
}

function unavailable(
  status: Exclude<SessionStartBaselineStatus, 'ready'>,
  sourceAt: number | null = null,
): SessionStartBaselineEvaluation {
  return { status, baseline: null, sourceAt }
}
